(() => {
  const FORM_SELECTOR = '[data-hkd-song-form]';

  const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, index);
    return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
  };

  const createReference = () => {
    const now = new Date();
    const date = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('');

    let randomPart = '';
    if (window.crypto && window.crypto.getRandomValues) {
      const values = new Uint32Array(1);
      window.crypto.getRandomValues(values);
      randomPart = values[0].toString(36).slice(0, 6).toUpperCase();
    } else {
      randomPart = Math.random().toString(36).slice(2, 8).toUpperCase();
    }

    return `HD-${date}-${randomPart.padEnd(6, '0')}`;
  };

  const getFocusableFields = (container) =>
    Array.from(container.querySelectorAll('input, select, textarea')).filter(
      (field) => !field.disabled && field.type !== 'hidden' && !field.closest('.hkd-honeypot')
    );

  const initializeForm = (form) => {
    if (form.dataset.hkdInitialized === 'true') return;
    form.dataset.hkdInitialized = 'true';
    form.dataset.enhanced = 'true';

    const root = form.closest('[data-hkd-request-root]') || form.parentElement;
    const steps = Array.from(form.querySelectorAll('[data-hkd-step]'));
    const progress = root.querySelector('[data-hkd-progress]');
    const progressItems = progress ? Array.from(progress.querySelectorAll('[data-hkd-progress-item]')) : [];
    const previousButtons = Array.from(form.querySelectorAll('[data-hkd-prev]'));
    const nextButtons = Array.from(form.querySelectorAll('[data-hkd-next]'));
    const submitButton = form.querySelector('[data-hkd-submit]');
    const fileInput = form.querySelector('[data-hkd-files]');
    const fileList = form.querySelector('[data-hkd-file-list]');
    const fileError = form.querySelector('[data-hkd-file-error]');
    const dropzone = form.querySelector('[data-hkd-dropzone]');
    const referenceInput = form.querySelector('[data-hkd-reference-input]');
    const pageUrlInput = form.querySelector('[data-hkd-page-url]');
    const dateInput = form.querySelector('input[type="date"][data-hkd-min-today]');

    let currentStep = 0;
    let furthestStep = 0;

    const reference = referenceInput?.value || createReference();
    if (referenceInput) referenceInput.value = reference;
    root.querySelectorAll('[data-hkd-reference-display]').forEach((element) => {
      element.textContent = reference;
    });

    if (pageUrlInput) pageUrlInput.value = window.location.href;

    if (dateInput && !dateInput.min) {
      const today = new Date();
      const isoToday = [
        today.getFullYear(),
        String(today.getMonth() + 1).padStart(2, '0'),
        String(today.getDate()).padStart(2, '0'),
      ].join('-');
      dateInput.min = isoToday;
    }

    if (progress) progress.hidden = false;
    previousButtons.forEach((button) => {
      button.hidden = false;
    });
    nextButtons.forEach((button) => {
      button.hidden = false;
    });

    const updateStep = (stepIndex, focusHeading = true) => {
      currentStep = Math.max(0, Math.min(stepIndex, steps.length - 1));
      furthestStep = Math.max(furthestStep, currentStep);

      steps.forEach((step, index) => {
        const isActive = index === currentStep;
        step.classList.toggle('is-active', isActive);
        step.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      progressItems.forEach((item, index) => {
        item.classList.toggle('is-active', index === currentStep);
        item.classList.toggle('is-complete', index < currentStep);
        if (index === currentStep) {
          item.setAttribute('aria-current', 'step');
        } else {
          item.removeAttribute('aria-current');
        }
        item.disabled = index > furthestStep;
      });

      if (focusHeading) {
        const legend = steps[currentStep]?.querySelector('legend');
        if (legend) {
          legend.setAttribute('tabindex', '-1');
          legend.focus({ preventScroll: true });
          steps[currentStep].scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    const validateFiles = () => {
      if (!fileInput) return true;

      const files = Array.from(fileInput.files || []);
      const maxFiles = Number.parseInt(fileInput.dataset.maxFiles || '5', 10);
      const maxSizeMb = Number.parseFloat(fileInput.dataset.maxSizeMb || '30');
      const maxTotalMb = Number.parseFloat(fileInput.dataset.maxTotalMb || '100');
      const maxSizeBytes = maxSizeMb * 1024 * 1024;
      const maxTotalBytes = maxTotalMb * 1024 * 1024;
      const accepted = (fileInput.getAttribute('accept') || '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
      const errors = [];
      const totalBytes = files.reduce((sum, file) => sum + file.size, 0);

      if (files.length > maxFiles) {
        errors.push(`Puedes adjuntar un máximo de ${maxFiles} archivos.`);
      }

      files.forEach((file) => {
        const dotIndex = file.name.lastIndexOf('.');
        const extension = dotIndex >= 0 ? file.name.slice(dotIndex).toLowerCase() : '';
        const allowedByExtension = accepted.length === 0 || accepted.includes(extension) || accepted.includes(file.type);

        if (!allowedByExtension) {
          errors.push(`El formato de «${file.name}» no está permitido.`);
        }

        if (file.size > maxSizeBytes) {
          errors.push(`«${file.name}» supera el límite de ${maxSizeMb} MB.`);
        }
      });

      if (totalBytes > maxTotalBytes) {
        errors.push(`El conjunto de archivos supera el límite total de ${maxTotalMb} MB.`);
      }

      const message = errors.join(' ');
      fileInput.setCustomValidity(message);
      if (fileError) {
        fileError.textContent = message;
        fileError.hidden = errors.length === 0;
      }
      if (dropzone) dropzone.classList.toggle('has-error', errors.length > 0);

      if (fileList) {
        fileList.replaceChildren();
        files.forEach((file) => {
          const item = document.createElement('li');
          const name = document.createElement('span');
          const size = document.createElement('span');
          name.textContent = file.name;
          size.textContent = formatBytes(file.size);
          size.className = 'hkd-file-list__size';
          item.append(name, size);
          fileList.append(item);
        });
      }

      return errors.length === 0;
    };

    const getInvalidField = (stepIndex) => {
      const step = steps[stepIndex];
      if (!step) return null;

      if (step.contains(fileInput) && !validateFiles()) {
        return fileInput;
      }

      const fields = getFocusableFields(step);
      fields.forEach((field) => {
        field.setAttribute('aria-invalid', field.checkValidity() ? 'false' : 'true');
      });

      return fields.find((field) => !field.checkValidity()) || null;
    };

    const validateStep = (stepIndex, report = true) => {
      const invalidField = getInvalidField(stepIndex);
      if (!invalidField) return true;

      if (report) {
        invalidField.reportValidity();
        invalidField.focus();
      }
      return false;
    };

    nextButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (validateStep(currentStep)) updateStep(currentStep + 1);
      });
    });

    previousButtons.forEach((button) => {
      button.addEventListener('click', () => updateStep(currentStep - 1));
    });

    progressItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        if (index < currentStep) {
          updateStep(index);
          return;
        }

        if (index > currentStep && index <= furthestStep && validateStep(currentStep)) {
          updateStep(index);
        }
      });
    });

    if (fileInput) fileInput.addEventListener('change', validateFiles);

    if (dropzone) {
      ['dragenter', 'dragover'].forEach((eventName) => {
        dropzone.addEventListener(eventName, () => dropzone.classList.add('is-dragging'));
      });
      ['dragleave', 'drop'].forEach((eventName) => {
        dropzone.addEventListener(eventName, () => dropzone.classList.remove('is-dragging'));
      });
    }

    form.addEventListener('submit', (event) => {
      const honeypot = form.querySelector('[data-hkd-honeypot]');
      if (honeypot?.value) {
        event.preventDefault();
        return;
      }

      let invalidStepIndex = -1;
      let invalidField = null;
      steps.some((_, index) => {
        const field = getInvalidField(index);
        if (field) {
          invalidStepIndex = index;
          invalidField = field;
          return true;
        }
        return false;
      });

      if (invalidStepIndex >= 0) {
        event.preventDefault();
        updateStep(invalidStepIndex, false);
        window.requestAnimationFrame(() => {
          invalidField?.reportValidity();
          invalidField?.focus();
        });
        return;
      }

      if (pageUrlInput) pageUrlInput.value = window.location.href;

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
        const submittingText = submitButton.dataset.submittingText;
        if (submittingText) submitButton.textContent = submittingText;
      }
    });

    updateStep(0, false);
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll(FORM_SELECTOR).forEach(initializeForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
